import { useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from "react";

import {
  AlertCircle,
  CheckCircle2,
  ClipboardCheck,
  FileCheck2,
  Rocket,
  Users,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import {
  AdminLaunchReadinessProjection,
  activateApprovalSnapshot,
  approveApprovalSnapshot,
  applyEvidenceContractOverride,
  createStaffUser,
  listAdminLaunchReadiness,
  listReviewTemplates,
  listStaffUsers,
  publishApprovalSnapshot,
  confirmEvidenceContract,
  EvidenceContractOverridePayload,
  EvidenceContractProjection,
  EvidenceRequirementProjection,
  EvidenceOverrideType,
  provisionWorkflowConfig,
  reactivateStaffUser,
  readEvidenceContract,
  requestStaffVerification,
  refreshApprovalSnapshot,
  ReviewTemplateProjection,
  StaffRoleValue,
  StaffUserProjection,
  verifyStaffUserWithOtp,
} from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getTenantIdFromStorage, getTenantSlugFromStorage } from "@/utils/auth";

export default function AdminOperationalLanding() {
  const location = useLocation();
  const [staff, setStaff] = useState<StaffUserProjection[]>([]);
  const [templates, setTemplates] = useState<ReviewTemplateProjection[]>([]);
  const [readinessRows, setReadinessRows] = useState<AdminLaunchReadinessProjection[]>(
    []
  );
  const [evidenceContracts, setEvidenceContracts] = useState<
    Record<string, EvidenceContractProjection>
  >({});
  const [overrideDrafts, setOverrideDrafts] = useState<
    Record<string, EvidenceContractOverridePayload>
  >({});
  const [fieldDecisions, setFieldDecisions] = useState<
    Record<string, Record<string, SavedFieldDecision>>
  >({});
  const [staffForm, setStaffForm] = useState<AdminStaffCreateForm>(INITIAL_ADMIN_STAFF_FORM);
  const [staffOtps, setStaffOtps] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantId = useMemo(() => {
    const raw = getTenantIdFromStorage();
    const parsed = raw ? Number(raw) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, []);
  const tenantSlug = getTenantSlugFromStorage();

  const activeTemplates = templates.filter((template) => template.is_active).length;
  const staffReadiness = buildStaffReadiness(staff);
  const adminActionRows = readinessRows.filter(
    (row) => row.status === "admin_provisioning_required"
  ).length;
  const readyRows = readinessRows.filter((row) => row.status === "ready_for_launch").length;
  const attentionCount = readinessRows.reduce(
    (total, row) =>
      total +
      buildAttentionItems(
        row,
        staffReadiness,
        fieldDecisions[readinessRowKey(row)] ?? {}
      ).length,
    0
  );
  const primaryScholarship = readinessRows[0]
    ? `${friendlyProgramName(readinessRows[0].program_code)} ${readinessRows[0].academic_year}`
    : "Scholarship";

  useEffect(() => {
    async function load() {
      if (!tenantId) {
        setError("Tenant context is missing. Log in again from the organization URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [staffRows, templateRows, readiness] = await Promise.all([
          listStaffUsers(tenantId),
          listReviewTemplates(),
          listAdminLaunchReadiness(tenantId),
        ]);
        setStaff(staffRows);
        setTemplates(templateRows);
        setReadinessRows(readiness);
      } catch (caught) {
        setError(readError(caught, "Could not load the Admin workspace."));
      } finally {
        setLoading(false);
      }
    }

    void load();
  }, [tenantId]);

  useEffect(() => {
    if (loading || !location.hash) {
      return;
    }
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: "start" });
  }, [loading, location.hash]);

  async function provisionSelectedReviewProcess(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const result = await provisionWorkflowConfig({
        tenant_id: tenantId,
        program_code: row.program_code,
        academic_year: row.academic_year,
      });
      setMessage(
        `Review process set up for ${result.program_code} ${result.academic_year}.`
      );
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
    } catch (caught) {
      setError(
        readError(
          caught,
          "Could not set up the selected review process. Confirm PM selected a review process first."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  async function publishSelectedSnapshot(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    if (!row.activation_candidate_snapshot_id) {
      setError("No approved student form is available to publish.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const result = await publishApprovalSnapshot(row.activation_candidate_snapshot_id);
      setMessage(`Published ${result.name} as the canonical form for ${result.academic_year}.`);
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
    } catch (caught) {
      setError(
        readError(
          caught,
          "Could not publish the student form. Confirm Admin approval and document rules are complete."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  async function activateSelectedSnapshot(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    if (!row.activation_candidate_snapshot_id) {
      setError("No published student form is available to open.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const result = await activateApprovalSnapshot(row.activation_candidate_snapshot_id);
      setMessage(`Activated ${result.program_code} ${result.academic_year} for applicant intake.`);
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
    } catch (caught) {
      setError(
        readError(
          caught,
          "Could not open applications. Confirm the student form is published and approved."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  async function prepareLatestLaunchEvidence(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    if (!row.activation_candidate_form_draft_id) {
      setError("No form draft is available to prepare for launch.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const snapshot = await refreshApprovalSnapshot(row.activation_candidate_form_draft_id);
      const approval = await approveApprovalSnapshot(snapshot.id);
      setMessage(
        approval.activation_preconditions_satisfied
          ? "Latest launch evidence is approved. You can now publish the student form."
          : "Latest launch evidence was refreshed and approved, but another launch check is still blocking publication."
      );
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
    } catch (caught) {
      setError(
        readError(
          caught,
          "Could not prepare the latest launch evidence. Check document rules and launch setup, then try again."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  async function reviewEvidenceContract(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const contract = await readEvidenceContract(
        tenantId,
        row.program_code,
        row.academic_year
      );
      const key = readinessRowKey(row);
      setEvidenceContracts((current) => ({ ...current, [key]: contract }));
      setOverrideDrafts((current) => ({
        ...current,
        [key]: current[key] ?? defaultOverrideDraft(row),
      }));
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
      setMessage(`Document requirements loaded for ${row.program_code} ${row.academic_year}.`);
    } catch (caught) {
      setError(readError(caught, "Could not load document requirements."));
    } finally {
      setSaving(false);
    }
  }

  async function confirmEvidenceForRow(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const contract = await confirmEvidenceContract(
        tenantId,
        row.program_code,
        row.academic_year,
        "Admin approved student document requirements for launch readiness."
      );
      setEvidenceContracts((current) => ({
        ...current,
        [readinessRowKey(row)]: contract,
      }));
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
      setMessage(`Document requirements approved for ${row.program_code} ${row.academic_year}.`);
    } catch (caught) {
      setError(readError(caught, "Could not approve document requirements."));
    } finally {
      setSaving(false);
    }
  }

  async function applyOverrideForRow(row: AdminLaunchReadinessProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    const key = readinessRowKey(row);
    const draft = overrideDrafts[key] ?? defaultOverrideDraft(row);
    const validationError = validateOverrideDraft(draft, row);
    if (validationError) {
      setMessage(null);
      setError(validationError);
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const contract = await applyEvidenceContractOverride(
        tenantId,
        row.program_code,
        row.academic_year,
        draft
      );
      setEvidenceContracts((current) => ({ ...current, [key]: contract }));
      setOverrideDrafts((current) => ({ ...current, [key]: defaultOverrideDraft(row) }));
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
      setMessage(`Document requirement updated for ${row.program_code} ${row.academic_year}.`);
    } catch (caught) {
      setError(readError(caught, "Could not save the document requirement change."));
    } finally {
      setSaving(false);
    }
  }

  async function saveEvidenceDecisionForRow(
    row: AdminLaunchReadinessProjection,
    decision: FieldDecisionSubmit
  ) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    const validationError = validateFieldDecision(decision);
    if (validationError) {
      setMessage(null);
      setError(validationError);
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    const key = readinessRowKey(row);
    setFieldDecisions((current) => ({
      ...current,
      [key]: {
        ...(current[key] ?? {}),
        [decision.fieldKey]: { ...decision, pending: true },
      },
    }));
    try {
      const payloads = buildDecisionPayloads(decision);
      let latestContract: EvidenceContractProjection | null = null;
      for (const payload of payloads) {
        latestContract = await applyEvidenceContractOverride(
          tenantId,
          row.program_code,
          row.academic_year,
          payload
        );
      }
      if (latestContract) {
        setEvidenceContracts((current) => ({
          ...current,
          [key]: latestContract,
        }));
      }
      setFieldDecisions((current) => ({
        ...current,
        [key]: {
          ...(current[key] ?? {}),
          [decision.fieldKey]: { ...decision, pending: false },
        },
      }));
      setReadinessRows(await listAdminLaunchReadiness(tenantId));
      setMessage(`Saved document decision for ${friendlyKey(decision.requirementKey)}.`);
    } catch (caught) {
      setFieldDecisions((current) => {
        const rowDecisions = { ...(current[key] ?? {}) };
        delete rowDecisions[decision.fieldKey];
        return { ...current, [key]: rowDecisions };
      });
      setError(
        readError(
          caught,
          "Could not save that document decision. Confirm the field and proof choice are valid."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  function updateOverrideDraft(
    row: AdminLaunchReadinessProjection,
    patch: Partial<EvidenceContractOverridePayload>
  ) {
    const key = readinessRowKey(row);
    setOverrideDrafts((current) => ({
      ...current,
      [key]: {
        ...(current[key] ?? defaultOverrideDraft(row)),
        ...patch,
        ...(patch.override_type ? defaultFieldsForOverrideType(patch.override_type) : {}),
      },
    }));
  }

  async function createStaffAndSendVerification() {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    const username = staffForm.username.trim();
    const email = staffForm.email.trim();
    const phone = staffForm.phone.trim();
    if (!USERNAME_PATTERN.test(username) || username.length < 3) {
      setError("Username can use letters, numbers, dot, underscore, or hyphen. No spaces.");
      return;
    }
    if (!email || !phone || !staffForm.password) {
      setError("Enter username, email, phone, role, and temporary password.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const created = await createStaffUser({
        tenant_id: tenantId,
        username,
        password: staffForm.password,
        role: staffForm.role,
        email,
        phone,
      });
      await requestStaffVerification(created.id);
      setStaffForm(INITIAL_ADMIN_STAFF_FORM);
      setStaff(await listStaffUsers(tenantId));
      setStaffOtps((current) => ({ ...current, [created.id]: "" }));
      setMessage(
        `${friendlyRole(staffForm.role)} user ${username} created. Verification code sent. Enter the OTP below to activate access.`
      );
    } catch (caught) {
      setError(readError(caught, "Could not create that staff user."));
    } finally {
      setSaving(false);
    }
  }

  async function sendStaffVerificationCode(userId: number) {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await requestStaffVerification(userId);
      setStaffOtps((current) => ({ ...current, [userId]: current[userId] ?? "" }));
      setMessage("Verification code sent.");
    } catch (caught) {
      setError(readError(caught, "Could not send verification code."));
    } finally {
      setSaving(false);
    }
  }

  async function verifyAndActivateStaff(user: StaffUserProjection) {
    if (!tenantId) {
      setError("Tenant context is missing. Log in again from the organization URL.");
      return;
    }
    const otp = (staffOtps[user.id] ?? "").trim();
    if (!user.is_verified && !otp) {
      setError("Enter the verification OTP before activating this user.");
      return;
    }
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      if (!user.is_verified) {
        await verifyStaffUserWithOtp(user.id, otp);
      }
      await reactivateStaffUser(
        user.id,
        "Activated from Admin launch readiness workspace."
      );
      setStaff(await listStaffUsers(tenantId));
      setStaffOtps((current) => {
        const next = { ...current };
        delete next[user.id];
        return next;
      });
      setMessage(`${friendlyRole(user.role)} user ${user.username} is active.`);
    } catch (caught) {
      setError(readError(caught, "Could not verify and activate that staff user."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-4 overflow-hidden px-4 py-4 sm:px-6">
      <header>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {tenantSlug || "Organization"} · Admin control centre
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-gray-950">
          {primaryScholarship} setup
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          See what is ready, who owns each remaining step, and what is blocked before students can apply.
        </p>
      </header>

      <nav
        aria-label="Admin workspace sections"
        className="flex flex-wrap gap-2 rounded-lg border bg-white p-2 text-sm"
      >
        <a
          href="#scholarship-setup"
          className="rounded-md px-3 py-2 font-medium text-gray-700 hover:bg-gray-100"
        >
          Scholarship Setup
        </a>
        <a
          href="#users-roles"
          className="rounded-md px-3 py-2 font-medium text-gray-700 hover:bg-gray-100"
        >
          Users & Roles
        </a>
      </nav>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}
      {message ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {message}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-lg border bg-white p-4 text-sm text-gray-600">
          Loading Admin workspace...
        </div>
      ) : (
        <>
          <section className="grid min-w-0 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            <MetricCard
              icon={<Rocket className="h-4 w-4" />}
              label="Ready for final open"
              value={`${readyRows}`}
              detail={`${readinessRows.length} scholarship${readinessRows.length === 1 ? "" : "s"} tracked`}
            />
            <MetricCard
              icon={<AlertCircle className="h-4 w-4" />}
              label="Remaining setup items"
              value={`${attentionCount}`}
              detail={adminActionRows > 0 ? "Review process setup" : "Before students can apply"}
            />
            <MetricCard
              icon={<Users className="h-4 w-4" />}
              label="Stages covered"
              value={`${staffReadiness.readyCount}/${staffReadiness.roles.length}`}
              detail="Application stages ready"
            />
            <MetricCard
              icon={<ClipboardCheck className="h-4 w-4" />}
              label="Approved standards"
              value={`${activeTemplates}`}
              detail="Review processes available"
            />
          </section>

          {readinessRows.length === 0 ? (
            <Card>
              <CardContent className="p-5 text-sm text-gray-600">
                No scholarship is waiting for launch setup.
              </CardContent>
            </Card>
          ) : (
            <section id="scholarship-setup" className="scroll-mt-6 space-y-4">
              {readinessRows.map((row) => (
                <ScholarshipLaunchCard
                  key={readinessRowKey(row)}
                  row={row}
                  staffReadiness={staffReadiness}
                  saving={saving}
                  contract={evidenceContracts[readinessRowKey(row)]}
                  savedDecisions={fieldDecisions[readinessRowKey(row)] ?? {}}
                  overrideDraft={
                    overrideDrafts[readinessRowKey(row)] ?? defaultOverrideDraft(row)
                  }
                  onProvision={() => void provisionSelectedReviewProcess(row)}
                  onPublish={() => void publishSelectedSnapshot(row)}
                  onActivate={() => void activateSelectedSnapshot(row)}
                  onPrepareLatestLaunchEvidence={() => void prepareLatestLaunchEvidence(row)}
                  onReviewEvidence={() => void reviewEvidenceContract(row)}
                  onConfirmEvidence={() => void confirmEvidenceForRow(row)}
                  onApplyOverride={() => void applyOverrideForRow(row)}
                  onOverrideChange={(patch) => updateOverrideDraft(row, patch)}
                  onSaveEvidenceDecision={(decision) =>
                    void saveEvidenceDecisionForRow(row, decision)
                  }
                />
              ))}
            </section>
          )}

          <section
            id="users-roles"
            className="scroll-mt-6 grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]"
          >
            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>Users & Roles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Tenant staff access for Organization Admin, PM, DDO, Project Coordinator,
                  Finance, and Management.
                </p>
                <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-blue-900">
                  Admin can create and activate tenant-scoped staff here. This does not grant
                  Super Admin access or cross-tenant authority.
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {staffReadiness.roles.map((role) => (
                    <StatusTile
                      key={role.role}
                      title={role.label}
                      state={role.ready ? "complete" : "attention"}
                      detail={
                        role.ready
                          ? `${role.activeCount} active`
                          : "No active user assigned"
                      }
                    />
                  ))}
                </div>
                <form
                  className="grid gap-3 rounded-lg border bg-gray-50 p-3 md:grid-cols-2 xl:grid-cols-5"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void createStaffAndSendVerification();
                  }}
                >
                  <div className="md:col-span-2 xl:col-span-5">
                    <div className="font-medium text-gray-950">Add a staff user</div>
                    <p className="text-xs text-gray-600">
                      Use this to add missing Organization Admin, PM, DDO, Project Coordinator,
                      Finance, or Management users. A verification code is required before access
                      becomes active.
                    </p>
                  </div>
                  <AdminTextInput
                    label="Username"
                    value={staffForm.username}
                    onChange={(value) => setStaffForm((current) => ({ ...current, username: value }))}
                  />
                  <AdminTextInput
                    label="Email"
                    type="email"
                    value={staffForm.email}
                    onChange={(value) => setStaffForm((current) => ({ ...current, email: value }))}
                  />
                  <AdminTextInput
                    label="Phone"
                    value={staffForm.phone}
                    placeholder="10 digits"
                    onChange={(value) => setStaffForm((current) => ({ ...current, phone: value }))}
                  />
                  <label className="grid gap-1 text-xs font-medium text-gray-700">
                    Role
                    <select
                      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-950"
                      value={staffForm.role}
                      onChange={(event) =>
                        setStaffForm((current) => ({
                          ...current,
                          role: event.target.value as StaffRoleValue,
                        }))
                      }
                    >
                      {ADMIN_STAFF_ROLE_OPTIONS.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <AdminTextInput
                    label="Temporary password"
                    type="password"
                    value={staffForm.password}
                    onChange={(value) => setStaffForm((current) => ({ ...current, password: value }))}
                  />
                  <div className="text-xs text-gray-600 md:col-span-2 xl:col-span-5">
                    Password must be 8 to 128 characters. Include at least one letter and one
                    number. Uppercase, lowercase, and special characters are allowed but not
                    required.
                  </div>
                  <div className="md:col-span-2 xl:col-span-5">
                    <Button type="submit" size="sm" disabled={saving}>
                      Create user and send verification code
                    </Button>
                  </div>
                </form>
                <PendingStaffActivation
                  staff={staff}
                  otps={staffOtps}
                  saving={saving}
                  onOtpChange={(userId, otp) =>
                    setStaffOtps((current) => ({ ...current, [userId]: otp }))
                  }
                  onSendCode={(userId) => void sendStaffVerificationCode(userId)}
                  onActivate={(user) => void verifyAndActivateStaff(user)}
                />
              </CardContent>
            </Card>

            <Card className="min-w-0">
              <CardHeader>
                <CardTitle>Who Does What</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 text-sm text-gray-600">
                <p>PM prepares the scholarship and chooses the review process.</p>
                <p>Admin checks documents, sets up review, publishes the form, then opens applications.</p>
                <p>DDO, Management, and Finance take over only after students submit applications.</p>
              </CardContent>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

function ScholarshipLaunchCard({
  row,
  staffReadiness,
  contract,
  savedDecisions,
  overrideDraft,
  saving,
  onProvision,
  onPublish,
  onActivate,
  onPrepareLatestLaunchEvidence,
  onReviewEvidence,
  onConfirmEvidence,
  onApplyOverride,
  onOverrideChange,
  onSaveEvidenceDecision,
}: {
  row: AdminLaunchReadinessProjection;
  staffReadiness: StaffReadinessSummary;
  contract?: EvidenceContractProjection;
  savedDecisions: Record<string, SavedFieldDecision>;
  overrideDraft: EvidenceContractOverridePayload;
  saving: boolean;
  onProvision: () => void;
  onPublish: () => void;
  onActivate: () => void;
  onPrepareLatestLaunchEvidence: () => void;
  onReviewEvidence: () => void;
  onConfirmEvidence: () => void;
  onApplyOverride: () => void;
  onOverrideChange: (patch: Partial<EvidenceContractOverridePayload>) => void;
  onSaveEvidenceDecision: (decision: FieldDecisionSubmit) => void;
}) {
  const checklist = buildLaunchChecklist(row, staffReadiness, savedDecisions);
  const completedChecks = checklist.filter((item) => item.state === "complete").length;
  const readinessScore = Math.round((completedChecks / checklist.length) * 100);
  const attentionItems = buildAttentionItems(row, staffReadiness, savedDecisions);
  const primary = primaryActionForRow(row, {
    onProvision,
    onPublish,
    onActivate,
    onPrepareLatestLaunchEvidence,
  });
  const canLaunch = row.status === "ready_for_launch" || row.activation_status === "active";

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardContent className="space-y-5 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Scholarship
            </div>
            <h2 className="mt-1 text-xl font-semibold text-gray-950">
              {friendlyProgramName(row.program_code)} {row.academic_year}
            </h2>
            <p className="mt-1 text-sm text-gray-600">{friendlyRowMessage(row)}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={row.status} />
            <ActivationStatusPill status={row.activation_status} />
          </div>
        </div>

        <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
          <div className="min-w-0 space-y-4">
            <div className="grid min-w-0 gap-3 lg:grid-cols-2">
              {checklist.map((item) => (
                <StatusTile
                  key={item.title}
                  title={item.title}
                  detail={item.detail}
                  state={item.state}
                  icon={item.icon}
                  owner={item.owner}
                  status={item.status}
                  action={item.action}
                />
              ))}
            </div>

            <DocumentRequirementsPanel
              row={row}
              contract={contract}
              savedDecisions={savedDecisions}
              overrideDraft={overrideDraft}
              saving={saving}
              onReview={onReviewEvidence}
              onConfirm={onConfirmEvidence}
              onApplyOverride={onApplyOverride}
              onOverrideChange={onOverrideChange}
              onSaveDecision={onSaveEvidenceDecision}
            />
          </div>

          <aside className="min-w-0 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Setup progress
              </div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-semibold text-gray-950">
                  {completedChecks}/{checklist.length}
                </span>
                <span className="pb-1 text-sm text-gray-600">
                  checks complete
                </span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full rounded-full ${canLaunch ? "bg-green-600" : "bg-indigo-600"}`}
                  style={{ width: `${readinessScore}%` }}
                />
              </div>
            </div>

            <div className="rounded-md border bg-white p-3">
              <div className="text-sm font-medium text-gray-950">Next step</div>
              <p className="mt-1 text-sm text-gray-600">{primary.description}</p>
              {primary.action ? (
                <>
                  <Button
                    type="button"
                    className="mt-3 w-full gap-2"
                    disabled={saving || primary.disabled}
                    onClick={primary.action}
                  >
                    <Rocket className="h-4 w-4" />
                    {primary.label}
                  </Button>
                  {primary.disabled && primary.disabledReason ? (
                    <p className="mt-2 text-xs text-amber-700">
                      This unlocks after: {primary.disabledReason}
                    </p>
                  ) : null}
                  {primary.disabled && row.status === "form_publication_required" ? (
                    <div className="mt-3 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                      <div className="font-semibold">To unlock publish</div>
                      <p>
                        Prepare the latest launch evidence after the setup changes. This refreshes
                        the evidence and records Admin approval.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={saving || !row.activation_candidate_form_draft_id}
                        onClick={onPrepareLatestLaunchEvidence}
                      >
                        Prepare latest evidence for publishing
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-2 text-sm font-medium text-green-800">
                  {primary.label}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm font-medium text-gray-950">
                Still needed before opening
              </div>
              {attentionItems.length === 0 ? (
                <div className="mt-2 rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-800">
                  Nothing blocking this scholarship.
                </div>
              ) : (
                <ul className="mt-2 space-y-2 text-sm text-gray-700">
                  {attentionItems.map((item) => (
                    <li key={item} className="flex gap-2">
                      <AlertCircle className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentRequirementsPanel({
  row,
  contract,
  savedDecisions,
  overrideDraft,
  saving,
  onReview,
  onConfirm,
  onApplyOverride,
  onOverrideChange,
  onSaveDecision,
}: {
  row: AdminLaunchReadinessProjection;
  contract?: EvidenceContractProjection;
  savedDecisions: Record<string, SavedFieldDecision>;
  overrideDraft: EvidenceContractOverridePayload;
  saving: boolean;
  onReview: () => void;
  onConfirm: () => void;
  onApplyOverride: () => void;
  onOverrideChange: (patch: Partial<EvidenceContractOverridePayload>) => void;
  onSaveDecision: (decision: FieldDecisionSubmit) => void;
}) {
  const [activeField, setActiveField] = useState<string | null>(null);
  const [draft, setDraft] = useState<FieldDecisionDraft | null>(null);

  if (row.evidence_requirements_status === "not_available") {
    return (
      <section className="rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-950">
          <FileCheck2 className="h-4 w-4" />
          Required Documents
        </div>
        <p className="mt-2 text-sm text-gray-600">
          No document requirements are available yet. Ask PM to finish the student form first.
        </p>
      </section>
    );
  }

  const requirements = row.evidence_requirements.resolved_requirements;
  const availableRequirements = availableEvidenceRequirements(row, contract);
  const savedDecisionList = Object.values(savedDecisions);
  const missingFields = unresolvedFieldsAfterLocalDecisions(row, savedDecisions);
  const selectedRequirement = draft?.requirementKey
    ? availableRequirements.find((item) => item.requirement_key === draft.requirementKey)
    : null;
  const selectedAffectedFields = draft
    ? affectedFieldsForRequirement(row, contract, draft.requirementKey, draft.fieldKey)
    : [];

  function openFieldDecision(fieldKey: string) {
    setActiveField(fieldKey);
    setDraft(defaultFieldDecisionDraft(fieldKey));
    if (availableRequirements.length === 0) {
      void onReview();
    }
  }

  function updateDraft(patch: Partial<FieldDecisionDraft>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
  }

  function saveDraftDecision() {
    if (!draft) {
      return;
    }
    onSaveDecision({
      fieldKey: draft.fieldKey,
      trustChoice: draft.trustChoice,
      requirementKey: draft.requirementKey,
      documentTypeKeys: draft.documentTypeKeys,
      methodKeys: draft.methodKeys,
      reason: buildDecisionReason(draft),
    });
    setActiveField(null);
    setDraft(null);
  }

  return (
    <section className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-950">
            <FileCheck2 className="h-4 w-4" />
            Required Documents
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Decide what students must upload or whether their declaration is enough.
          </p>
        </div>
        <StatusPill status={documentRulesStatus(row, contract)} />
      </div>

      {requirements.length > 0 ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {requirements.map((requirement) => (
            <div key={requirement.requirement_key} className="rounded-md border bg-gray-50 p-3">
              <div className="font-medium text-gray-950">
                {friendlyKey(requirement.requirement_key)}
              </div>
              <div className="mt-2 text-sm text-gray-600">
                Covers {listOrNone(requirement.required_for_fields.map(friendlyKey))}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                Verification: {listOrNone(requirement.accepted_methods.map(friendlyKey))}
              </div>
              <div className="mt-1 text-sm text-gray-600">
                Student documents:{" "}
                {listOrNone(requirement.accepted_document_types.map(friendlyKey))}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {sourceLabel(requirement.source)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {savedDecisionList.length > 0 ? (
        <div className="mt-4 space-y-2 rounded-md border border-green-200 bg-green-50 p-3">
          <div className="text-sm font-semibold text-green-900">Decisions made</div>
          <div className="grid gap-2">
            {savedDecisionList.map((decision) => {
              const affectedFields = affectedFieldsForRequirement(
                row,
                contract,
                decision.requirementKey,
                decision.fieldKey
              );
              return (
                <div
                  key={decision.fieldKey}
                  className="flex flex-col gap-1 rounded-md border border-green-200 bg-white px-3 py-2 text-sm text-green-950 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <span className="font-medium">{friendlyKey(decision.requirementKey)}</span>
                    <span className="ml-2 text-xs text-green-700">
                      {decision.pending ? "Saving..." : "Decision saved"}
                    </span>
                    <div className="mt-1 text-xs text-green-800">
                      Applies to: {listOrNone(affectedFields.map(friendlyKey))}
                    </div>
                  </div>
                  <div className="text-xs text-green-800">
                    {fieldDecisionSummary(decision)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {missingFields.length > 0 ? (
        <div className="mt-4 space-y-3">
          <div className="flex flex-col gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Not yet decided
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Students cannot apply until every field below has a decision. Choose a field to
                finish its rule.
              </p>
            </div>
            <span className="w-fit rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
              {missingFields.length} remaining
            </span>
          </div>
          <div className="grid gap-2">
            {missingFields.map((fieldKey) => {
              return (
                <button
                  key={fieldKey}
                  type="button"
                  className={`flex flex-col gap-2 rounded-md border px-3 py-3 text-left text-sm transition ${
                    activeField === fieldKey
                      ? "border-blue-300 bg-blue-50 text-blue-950"
                      : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => openFieldDecision(fieldKey)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium">{friendlyKey(fieldKey)}</span>
                    <span className="text-xs text-gray-500">
                      {activeField === fieldKey ? "Decision open" : "Not decided yet"}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Proof category:{" "}
                    <span className="font-medium text-gray-800">choose when opening</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Admin must explicitly choose the proof category before saving.
                  </div>
                </button>
              );
            })}
          </div>
          {draft ? (
            <FieldDecisionWizard
              draft={draft}
              affectedFields={selectedAffectedFields}
              selectedRequirement={selectedRequirement}
              availableRequirements={availableRequirements}
              saving={saving}
              onChange={updateDraft}
              onSave={saveDraftDecision}
              onReloadRequirements={onReview}
            />
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:items-center">
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={onReview}>
          Load document rules
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={
            saving ||
            !(
              contract?.status === "READY_FOR_CONFIRMATION" ||
              row.evidence_requirements_status === "resolved"
            )
          }
          onClick={onConfirm}
        >
          Information requirements locked in
        </Button>
        <span className="text-xs text-gray-600">
          {contract ? contractStatusLabel(contract.status) : "Load rules before final approval"}
        </span>
      </div>

      <details className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
        <summary className="cursor-pointer text-sm font-medium text-gray-700">
          Advanced support information
        </summary>
        <EvidenceContractControls
          row={row}
          contract={contract}
          overrideDraft={overrideDraft}
          saving={saving}
          onReview={onReview}
          onConfirm={onConfirm}
          onApplyOverride={onApplyOverride}
          onOverrideChange={onOverrideChange}
        />
      </details>
    </section>
  );
}

function FieldDecisionWizard({
  draft,
  affectedFields,
  selectedRequirement,
  availableRequirements,
  saving,
  onChange,
  onSave,
  onReloadRequirements,
}: {
  draft: FieldDecisionDraft;
  affectedFields: string[];
  selectedRequirement?: EvidenceRequirementProjection | null;
  availableRequirements: EvidenceRequirementProjection[];
  saving: boolean;
  onChange: (patch: Partial<FieldDecisionDraft>) => void;
  onSave: () => void;
  onReloadRequirements: () => void;
}) {
  const steps = ["Category", "Documents", "Verification", "Confirm"] as const;
  const categoryLabel = selectedRequirement
    ? requirementDisplayName(selectedRequirement)
    : "proof category";
  const validationError = validateFieldDecision({
    fieldKey: draft.fieldKey,
    trustChoice: draft.trustChoice,
    requirementKey: draft.requirementKey,
    documentTypeKeys: draft.documentTypeKeys,
    methodKeys: draft.methodKeys,
    reason: buildDecisionReason(draft),
  });
  const requirementOptions = availableRequirements.map((requirement) => requirement.requirement_key);
  const requirementLabels = optionLabelsForRequirements(availableRequirements);
  const hasRequirementOptions = requirementOptions.length > 0;
  const availableDocumentOptions = Array.from(
    new Set([
      ...(selectedRequirement?.accepted_document_types.map(
        (document) => document.document_type_key
      ) ?? []),
    ].filter(Boolean))
  ).sort();
  const documentLabels = optionLabelsForDocumentTypes(selectedRequirement);
  const availableMethodOptions = Array.from(
    new Set([
      ...(selectedRequirement?.accepted_methods.map((method) => method.method_key) ?? []),
    ].filter(Boolean))
  ).sort();
  const methodLabels = optionLabelsForMethods(selectedRequirement);

  function goNext() {
    if (draft.step === 1 && (!draft.requirementKey || !draft.trustChoice)) {
      return;
    }
    if (draft.step === 1 && draft.trustChoice === "declaration") {
      onChange({ step: 4 });
      return;
    }
    if (draft.step === 2 && draft.documentTypeKeys.length === 0) {
      return;
    }
    if (draft.step === 3 && draft.methodKeys.length === 0) {
      return;
    }
    onChange({ step: Math.min(4, draft.step + 1) as FieldDecisionStep });
  }

  function goBack() {
    if (draft.step === 4 && draft.trustChoice === "declaration") {
      onChange({ step: 1 });
      return;
    }
    onChange({ step: Math.max(1, draft.step - 1) as FieldDecisionStep });
  }

  function toggleDocument(documentTypeKey: string) {
    const selected = draft.documentTypeKeys.includes(documentTypeKey);
    onChange({
      documentTypeKeys: selected
        ? draft.documentTypeKeys.filter((item) => item !== documentTypeKey)
        : [...draft.documentTypeKeys, documentTypeKey],
    });
  }

  function toggleMethod(methodKey: string) {
    const selected = draft.methodKeys.includes(methodKey);
    onChange({
      methodKeys: selected
        ? draft.methodKeys.filter((item) => item !== methodKey)
        : [...draft.methodKeys, methodKey],
    });
  }

  return (
    <div className="overflow-hidden rounded-lg border border-blue-200 bg-white">
      <div className="grid grid-cols-4 border-b border-gray-200 text-center text-[11px] font-semibold uppercase tracking-wide">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const tone =
            draft.step === stepNumber
              ? "bg-blue-50 text-blue-800"
              : draft.step > stepNumber
                ? "bg-green-50 text-green-800"
                : "bg-gray-50 text-gray-500";
          return (
            <div key={step} className={`border-r border-gray-200 px-2 py-2 last:border-r-0 ${tone}`}>
              {stepNumber}. {step}
            </div>
          );
        })}
      </div>

      <div className="space-y-4 p-4">
        {draft.step === 1 ? (
          <>
            <div>
              <div className="text-base font-semibold text-gray-950">
                Set up how {friendlyKey(draft.fieldKey)} will be checked
              </div>
              {hasRequirementOptions ? (
                <p className="mt-1 text-sm text-gray-600">
                  Choose the document category this information belongs to. This decides whether
                  students can simply declare it or must upload proof.
                </p>
              ) : (
                <p className="mt-1 text-sm text-gray-600">
                  Document categories have not loaded yet. Reload them, then choose the category
                  that should cover this information.
                </p>
              )}
              <div className="mt-3">
                <LabeledSelect
                  label="Document category"
                  value={draft.requirementKey}
                  placeholder="Choose a document category"
                  options={requirementOptions}
                  optionLabels={requirementLabels}
                  disabled={!hasRequirementOptions || saving}
                  onChange={(requirementKey) =>
                    onChange({
                      requirementKey,
                      documentTypeKeys: [],
                      methodKeys: [],
                    })
                  }
                />
              </div>
              {!hasRequirementOptions ? (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <div className="font-medium">Document categories did not load.</div>
                  <p className="mt-1">
                    This page needs the approved categories before Admin can decide whether a
                    declaration or document is enough.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-3"
                    disabled={saving}
                    onClick={onReloadRequirements}
                  >
                    Reload document categories
                  </Button>
                </div>
              ) : null}
              <p className="mt-3 text-sm text-gray-600">
                This decision will apply to: {listOrNone(affectedFields.map(friendlyKey))}.
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Will you accept the student declaration for this category, or must they
                provide a supporting document?
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <DecisionChoice
                selected={draft.trustChoice === "declaration"}
                title="Student declaration is sufficient"
                detail="No upload needed. Suitable for contact details or information that does not need proof before launch."
                disabled={!hasRequirementOptions}
                onClick={() =>
                  onChange({
                    trustChoice: "declaration",
                    documentTypeKeys: [],
                    methodKeys: [],
                  })
                }
              />
              <DecisionChoice
                selected={draft.trustChoice === "document"}
                title="Supporting document is required"
                detail="Student must upload a document. Suitable for income, location, academic results, or identity."
                disabled={!hasRequirementOptions}
                onClick={() =>
                  onChange({
                    trustChoice: "document",
                    methodKeys: draft.methodKeys.filter((item) => item !== "SELF_DECLARATION"),
                  })
                }
              />
            </div>
          </>
        ) : null}

        {draft.step === 2 ? (
          <>
            <div>
              <div className="text-base font-semibold text-gray-950">
                What can students upload for {categoryLabel}?
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Choose every document you are willing to accept. Students may submit any one of
                these for this proof category.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {availableDocumentOptions.map((option) => (
                <label
                  key={option}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                    draft.documentTypeKeys.includes(option)
                      ? "border-blue-300 bg-blue-50 text-blue-900"
                      : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={draft.documentTypeKeys.includes(option)}
                    onChange={() => toggleDocument(option)}
                  />
                  {documentLabels[option] ?? friendlyKey(option)}
                </label>
              ))}
            </div>
          </>
        ) : null}

        {draft.step === 3 ? (
          <>
            <div>
              <div className="text-base font-semibold text-gray-950">
                How will {categoryLabel} be verified?
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Choose every verification method DDO can use after the student submits this proof.
              </p>
            </div>
            <div className="grid gap-2">
              {availableMethodOptions.map((option) => (
                <label
                  key={option}
                  className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                    draft.methodKeys.includes(option)
                      ? "border-blue-300 bg-blue-50 text-blue-900"
                      : "border-gray-200 bg-white text-gray-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={draft.methodKeys.includes(option)}
                    onChange={() => toggleMethod(option)}
                  />
                  {methodLabels[option] ?? friendlyKey(option)}
                </label>
              ))}
            </div>
          </>
        ) : null}

        {draft.step === 4 ? (
          <div>
            <div className="text-base font-semibold text-gray-950">
              Confirm this decision for {categoryLabel}
            </div>
            <div className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              <div>
                <span className="font-medium">Affected fields:</span>{" "}
                {listOrNone(affectedFields.map(friendlyKey))}
              </div>
              <div>
                <span className="font-medium">Decision:</span>{" "}
                {draft.trustChoice === "declaration"
                  ? "Student declaration is sufficient"
                  : "Supporting document is required"}
              </div>
              {draft.trustChoice === "document" ? (
                <>
                  <div className="mt-2">
                    <span className="font-medium">Accepted documents:</span>{" "}
                    {listOrNone(draft.documentTypeKeys.map(friendlyKey))}
                  </div>
                  <div className="mt-2">
                    <span className="font-medium">Verification:</span>{" "}
                    {listOrNone(draft.methodKeys.map(friendlyKey))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {validationError && draft.step === 4 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
            {validationError}
          </div>
        ) : null}

        <div className="flex gap-2">
          {draft.step > 1 ? (
            <Button type="button" variant="outline" size="sm" onClick={goBack}>
              Back
            </Button>
          ) : null}
          {draft.step < 4 ? (
            <Button
              type="button"
              size="sm"
              className="flex-1"
              disabled={
                (draft.step === 1 &&
                  (!hasRequirementOptions || !draft.requirementKey || !draft.trustChoice)) ||
                (draft.step === 2 && draft.documentTypeKeys.length === 0) ||
                (draft.step === 3 && draft.methodKeys.length === 0)
              }
              onClick={goNext}
            >
              Continue
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="flex-1"
              disabled={saving || Boolean(validationError)}
              onClick={onSave}
            >
              Confirm this decision
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function DecisionChoice({
  selected,
  title,
  detail,
  disabled = false,
  onClick,
}: {
  selected: boolean;
  title: string;
  detail: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`rounded-md border p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? "border-blue-300 bg-blue-50 text-blue-950"
          : disabled
            ? "border-gray-200 bg-gray-50 text-gray-500"
            : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
      }`}
      onClick={onClick}
    >
      <div className="text-sm font-semibold">{title}</div>
      <p className="mt-1 text-xs leading-5 opacity-80">{detail}</p>
    </button>
  );
}

function EvidenceContractControls({
  row,
  contract,
  overrideDraft,
  saving,
  onReview,
  onConfirm,
  onApplyOverride,
  onOverrideChange,
}: {
  row: AdminLaunchReadinessProjection;
  contract?: EvidenceContractProjection;
  overrideDraft: EvidenceContractOverridePayload;
  saving: boolean;
  onReview: () => void;
  onConfirm: () => void;
  onApplyOverride: () => void;
  onOverrideChange: (patch: Partial<EvidenceContractOverridePayload>) => void;
}) {
  const blockers = contract?.current_revision.confirmation_blockers ?? [];
  const canConfirm = contract
    ? contract.status === "READY_FOR_CONFIRMATION"
    : row.evidence_requirements_status === "resolved";
  const fieldOptions = row.evidence_requirements.requested_fields;
  const availableRequirements = availableEvidenceRequirements(row, contract);
  const requirementOptions = availableRequirements.map(
    (requirement) => requirement.requirement_key
  );
  const requirementLabels = optionLabelsForRequirements(availableRequirements);
  const selectedRequirement = availableRequirements.find(
    (requirement) => requirement.requirement_key === overrideDraft.requirement_key
  );
  const methodOptions = evidenceMethodOptions(selectedRequirement, availableRequirements);
  const methodLabels = optionLabelsForMethods(selectedRequirement, availableRequirements);
  const documentOptions = evidenceDocumentOptions(selectedRequirement, availableRequirements);
  const documentLabels = optionLabelsForDocumentTypes(
    selectedRequirement,
    availableRequirements
  );
  const showField =
    overrideDraft.override_type === "map_field_requirement" ||
    overrideDraft.override_type === "remove_field_requirement";
  const showMethod =
    overrideDraft.override_type === "enable_method" ||
    overrideDraft.override_type === "disable_method";
  const showDocument =
    overrideDraft.override_type === "add_document_type" ||
    overrideDraft.override_type === "remove_document_type";
  const overrideValidationError = validateOverrideDraft(overrideDraft, row);

  function updateText(field: keyof EvidenceContractOverridePayload, value: string) {
    onOverrideChange({ [field]: value } as Partial<EvidenceContractOverridePayload>);
  }

  function updateInput(
    field: keyof EvidenceContractOverridePayload,
    event: ChangeEvent<HTMLInputElement>
  ) {
    onOverrideChange({ [field]: event.target.value } as Partial<
      EvidenceContractOverridePayload
    >);
  }

  return (
    <div className="mt-4 space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={onReview}>
          Load document rules
        </Button>
        <Button type="button" size="sm" disabled={saving || !canConfirm} onClick={onConfirm}>
          Approve document rules
        </Button>
        {contract ? (
          <span className="text-xs text-gray-600">
            {contractStatusLabel(contract.status)}
          </span>
        ) : null}
      </div>
      {blockers.length > 0 ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          <div className="font-medium">What still needs attention</div>
          <div className="mt-1">{blockers.map(friendlyBlocker).join(", ")}</div>
        </div>
      ) : null}
      {row.evidence_requirements_status !== "not_available" ? (
        <div className="grid gap-2 text-sm">
          <div className="text-xs text-gray-600">
            Use this to finish or adjust student document rules. It does not add or remove PM
            form fields.
          </div>
          <select
            className="rounded-md border border-gray-300 bg-white px-3 py-2"
            value={overrideDraft.override_type}
            onChange={(event) =>
              onOverrideChange({
                override_type: event.target.value as EvidenceOverrideType,
              })
            }
          >
            <option value="map_field_requirement">Set proof needed for a field</option>
            <option value="remove_field_requirement">Mark a field as no proof needed</option>
            <option value="enable_method">Allow another verification method</option>
            <option value="disable_method">Remove a verification method</option>
            <option value="add_document_type">Add an accepted document</option>
            <option value="remove_document_type">Remove an accepted document</option>
          </select>
          <div className="text-xs text-gray-600">
            {overrideHelpText(overrideDraft.override_type)}
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {showField ? (
              <LabeledSelect
                label="Form field"
                value={overrideDraft.field_key ?? ""}
                placeholder="Choose a field"
                onChange={(value) => updateText("field_key", value)}
                options={fieldOptions}
              />
            ) : null}
              <LabeledSelect
                label="Proof category"
                value={overrideDraft.requirement_key ?? ""}
                placeholder="Choose a proof category"
                onChange={(value) => updateText("requirement_key", value)}
                options={requirementOptions}
                optionLabels={requirementLabels}
              />
            {showMethod ? (
              <LabeledSelect
                label="Verification method"
                value={overrideDraft.method_key ?? ""}
                placeholder="Choose a method"
                onChange={(value) => updateText("method_key", value)}
                options={methodOptions}
                optionLabels={methodLabels}
              />
            ) : null}
            {showDocument ? (
              <LabeledSelect
                label="Student document"
                value={overrideDraft.document_type_key ?? ""}
                placeholder="Choose a document"
                onChange={(value) => updateText("document_type_key", value)}
                options={documentOptions}
                optionLabels={documentLabels}
              />
            ) : null}
          </div>
          <LabeledInput
            label="Reason for this change"
            placeholder="Example: District should be verified with address proof before launch"
            value={overrideDraft.reason}
            onChange={(event) => updateInput("reason", event)}
          />
          {overrideValidationError ? (
            <div className="text-xs text-amber-700">{overrideValidationError}</div>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={saving || Boolean(overrideValidationError)}
            onClick={onApplyOverride}
          >
            Save document rule change
          </Button>
        </div>
      ) : null}
    </div>
  );
}

type TileState = "complete" | "attention" | "waiting" | "blocked";
type FieldDecisionStep = 1 | 2 | 3 | 4;
type FieldTrustChoice = "declaration" | "document" | null;
const USERNAME_PATTERN = /^[a-zA-Z0-9._-]+$/;

const ADMIN_STAFF_ROLE_OPTIONS: { value: StaffRoleValue; label: string }[] = [
  { value: "admin", label: "Organization Admin" },
  { value: "pm", label: "PM" },
  { value: "ddo", label: "DDO" },
  { value: "pc", label: "Project Coordinator" },
  { value: "finance", label: "Finance" },
  { value: "management", label: "Management" },
];

const INITIAL_ADMIN_STAFF_FORM: AdminStaffCreateForm = {
  username: "",
  email: "",
  phone: "",
  role: "finance",
  password: "",
};

interface AdminStaffCreateForm {
  username: string;
  email: string;
  phone: string;
  role: StaffRoleValue;
  password: string;
}

interface FieldDecisionDraft {
  step: FieldDecisionStep;
  fieldKey: string;
  trustChoice: FieldTrustChoice;
  requirementKey: string;
  documentTypeKeys: string[];
  methodKeys: string[];
}

interface FieldDecisionSubmit {
  fieldKey: string;
  trustChoice: FieldTrustChoice;
  requirementKey: string;
  documentTypeKeys: string[];
  methodKeys: string[];
  reason: string;
}

interface SavedFieldDecision extends FieldDecisionSubmit {
  pending: boolean;
}

interface StaffRoleReadiness {
  role: string;
  label: string;
  activeCount: number;
  ready: boolean;
}

interface StaffReadinessSummary {
  roles: StaffRoleReadiness[];
  readyCount: number;
  allReady: boolean;
}

interface LaunchChecklistItem {
  title: string;
  detail: string;
  state: TileState;
  icon: ReactNode;
  owner?: string;
  status: string;
  action: string;
}

interface PrimaryAction {
  label: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
  action?: () => void;
}

function buildStaffReadiness(staff: StaffUserProjection[]): StaffReadinessSummary {
  const requiredRoles = [
    { role: "pm", label: "PM" },
    { role: "ddo", label: "DDO" },
    { role: "pc", label: "Project Coordinator" },
    { role: "finance", label: "Finance" },
    { role: "management", label: "Management" },
  ];
  const roles = requiredRoles.map((item) => {
    const activeCount = staff.filter(
      (user) =>
        user.role === item.role &&
        Boolean(user.is_active) &&
        Boolean(user.is_verified)
    ).length;
    return {
      ...item,
      activeCount,
      ready: activeCount > 0,
    };
  });
  const readyCount = roles.filter((role) => role.ready).length;
  return {
    roles,
    readyCount,
    allReady: readyCount === roles.length,
  };
}

function buildLaunchChecklist(
  row: AdminLaunchReadinessProjection,
  staffReadiness: StaffReadinessSummary,
  savedDecisions: Record<string, SavedFieldDecision> = {}
): LaunchChecklistItem[] {
  const reviewReady = Boolean(row.provisioned_workflow_config_id);
  const documentReady = documentRulesReadyForApproval(row, savedDecisions);
  const formApproved = Boolean(row.activation_candidate_snapshot_id);
  const formDraftAvailable = Boolean(row.activation_candidate_form_draft_id);
  const hasActivePm = staffReadiness.roles.some((role) => role.role === "pm" && role.ready);
  const missingRoles = staffReadiness.roles.filter((role) => !role.ready);
  const publishedReady =
    row.activation_status === "active" ||
    row.status === "form_activation_required" ||
    row.status === "ready_for_launch";
  const intakeOpen = row.activation_status === "active" || row.status === "ready_for_launch";

  return [
    {
      title: "Program Details",
      detail: `${friendlyProgramName(row.program_code)} for ${row.academic_year}`,
      state: "complete",
      icon: <ClipboardCheck className="h-4 w-4" />,
      owner: "Organization Admin",
      status: "Complete",
      action: "No action required",
    },
    {
      title: "Users & Roles",
      detail: staffReadiness.allReady
        ? "PM, DDO, Project Coordinator, Finance, and Management are active"
        : `${staffReadiness.readyCount}/${staffReadiness.roles.length} required roles have active users`,
      state: staffReadiness.allReady ? "complete" : "attention",
      icon: <Users className="h-4 w-4" />,
      owner: "Organization Admin",
      status: staffReadiness.allReady ? "Complete" : "Action required",
      action: staffReadiness.allReady
        ? "No action required"
        : `Assign active users for ${missingRoles.map((role) => role.label).join(", ")}`,
    },
    {
      title: "Application Form",
      detail: formApproved
        ? `${row.activation_candidate_form_name ?? "Student form"} is ${friendlyKey(row.activation_candidate_status ?? "available")}`
        : formDraftAvailable
          ? `${row.activation_candidate_form_name ?? "Student form"} is still waiting for launch approval`
          : !hasActivePm
            ? "No Program Manager assigned. Add one under Users & Roles."
          : "PM must create and prepare the student form",
      state: formApproved ? "complete" : formDraftAvailable ? "attention" : "waiting",
      icon: <FileCheck2 className="h-4 w-4" />,
      owner: "Program Manager",
      status: formApproved
        ? friendlyKey(row.activation_candidate_status ?? "Ready")
        : formDraftAvailable
          ? "Action required"
          : hasActivePm
            ? "Not started"
            : "Action required",
      action: formApproved
        ? "No action required"
        : formDraftAvailable
          ? "Waiting for Program Manager to complete the form handoff"
          : hasActivePm
            ? "Waiting for Program Manager"
            : "No Program Manager assigned",
    },
    {
      title: "Review Standard",
      detail: reviewReady
        ? `${row.provisioned_review_process_name ?? row.selected_review_process_name ?? "Selected process"} is set up`
        : row.selected_review_process_name
          ? `${row.selected_review_process_name} selected; Admin setup is required`
          : "PM must choose an approved review process",
      state: reviewReady ? "complete" : row.selected_review_process_name ? "attention" : "waiting",
      icon: <ClipboardCheck className="h-4 w-4" />,
      owner: "PM selects; Organization Admin provisions",
      status: reviewReady
        ? "Complete"
        : row.selected_review_process_name
          ? "Action required"
          : "Waiting on other role",
      action: reviewReady
        ? "No action required"
        : row.selected_review_process_name
          ? "Organization Admin must set up the selected review process"
          : "Waiting for Program Manager to select a review process",
    },
    {
      title: "Evidence Requirements",
      detail: documentRulesComplete(row)
        ? "Document rules are approved"
        : documentReady
          ? "Document decisions are ready for approval"
          : documentAttentionSummary(row, savedDecisions),
      state: documentRulesComplete(row) ? "complete" : documentReady ? "attention" : "blocked",
      icon: <FileCheck2 className="h-4 w-4" />,
      owner: row.evidence_requirements_status === "not_available" ? "Program Manager" : "Organization Admin",
      status: documentRulesComplete(row)
        ? "Complete"
        : row.evidence_requirements_status === "not_available"
          ? "Waiting on other role"
          : documentReady
            ? "Action required"
            : "Blocked",
      action: documentRulesComplete(row)
        ? "No action required"
        : row.evidence_requirements_status === "not_available"
          ? "Waiting for Program Manager to finish the application form"
          : "Organization Admin must finish evidence requirements",
    },
    {
      title: "Publish Application",
      detail: publishedReady
        ? "Student form is published"
        : row.activation_candidate_can_publish
          ? "Ready for Admin to publish"
          : row.activation_candidate_blocker
            ? friendlyBlocker(row.activation_candidate_blocker)
            : "Blocked until setup evidence is approved",
      state: publishedReady ? "complete" : row.activation_candidate_can_publish ? "attention" : "blocked",
      icon: <Rocket className="h-4 w-4" />,
      owner: "Organization Admin",
      status: publishedReady
        ? "Complete"
        : row.activation_candidate_can_publish
          ? "Action required"
          : "Blocked",
      action: publishedReady
        ? "No action required"
        : row.activation_candidate_can_publish
          ? "Organization Admin can publish the application"
          : "Resolve setup blockers before publishing",
    },
    {
      title: "Open Applications",
      detail: intakeOpen
        ? "Student applications can use this form"
        : launchAttentionSummary(row),
      state: intakeOpen ? "complete" : row.activation_candidate_blocker ? "blocked" : "attention",
      icon: <Rocket className="h-4 w-4" />,
      owner: "Organization Admin",
      status: intakeOpen
        ? "Complete"
        : row.activation_candidate_blocker
          ? "Blocked"
          : "Action required",
      action: intakeOpen
        ? "No action required"
        : row.activation_candidate_can_activate
          ? "Organization Admin can open applications"
          : "Resolve setup blockers before opening applications",
    },
  ];
}

function documentRulesComplete(row: AdminLaunchReadinessProjection): boolean {
  return (
    row.evidence_requirements_status === "resolved" &&
    row.evidence_contract_blockers.length === 0
  );
}

function documentRulesReadyForApproval(
  row: AdminLaunchReadinessProjection,
  savedDecisions: Record<string, SavedFieldDecision> = {}
): boolean {
  if (row.evidence_requirements_status === "not_available") {
    return false;
  }
  return documentRulesComplete(row) || unresolvedFieldsAfterLocalDecisions(row, savedDecisions).length === 0;
}

function unresolvedFieldsAfterLocalDecisions(
  row: AdminLaunchReadinessProjection,
  savedDecisions: Record<string, SavedFieldDecision>
): string[] {
  return row.evidence_requirements.unresolved_fields.filter(
    (fieldKey) => !savedDecisions[fieldKey]
  );
}

function documentAttentionSummary(
  row: AdminLaunchReadinessProjection,
  savedDecisions: Record<string, SavedFieldDecision> = {}
): string {
  if (row.evidence_requirements_status === "not_available") {
    return "PM form needs to be finished first";
  }
  const unresolvedFields = unresolvedFieldsAfterLocalDecisions(row, savedDecisions);
  if (unresolvedFields.length > 0) {
    return `${unresolvedFields.length} field${
      unresolvedFields.length === 1 ? "" : "s"
    } still need document decisions`;
  }
  if (row.evidence_contract_blockers.length > 0) {
    return row.evidence_contract_blockers.map(friendlyBlocker).join(", ");
  }
  return "Document rules need Admin approval";
}

function launchAttentionSummary(row: AdminLaunchReadinessProjection): string {
  if (row.activation_candidate_blocker) {
    return friendlyBlocker(row.activation_candidate_blocker);
  }
  if (row.status === "form_publication_required") {
    return "Publish the student form before opening applications";
  }
  if (row.status === "form_activation_required") {
    return "Open the published form for students";
  }
  if (row.status === "admin_provisioning_required") {
    return "Set up the selected review process first";
  }
  return friendlyRowMessage(row);
}

function buildAttentionItems(
  row: AdminLaunchReadinessProjection,
  staffReadiness: StaffReadinessSummary,
  savedDecisions: Record<string, SavedFieldDecision> = {}
): string[] {
  const items: string[] = [];
  if (!row.selected_review_process_name) {
    items.push("PM must choose a review process.");
  } else if (!row.provisioned_workflow_config_id) {
    items.push("Admin must set up the selected review process.");
  }
  if (row.evidence_requirements_status === "not_available") {
    items.push("PM must finish the student form before document rules can be checked.");
  }
  const unresolvedFields = unresolvedFieldsAfterLocalDecisions(row, savedDecisions);
  if (unresolvedFields.length > 0) {
    items.push(
      `${unresolvedFields
        .map(friendlyKey)
        .join(", ")} still need document decisions.`
    );
  }
  items.push(...row.evidence_contract_blockers.map(friendlyBlocker));
  if (row.activation_candidate_blocker) {
    items.push(friendlyBlocker(row.activation_candidate_blocker));
  }
  const missingRoles = staffReadiness.roles.filter((role) => !role.ready);
  if (missingRoles.length > 0) {
    items.push(
      `Assign active users for ${missingRoles.map((role) => role.label).join(", ")}.`
    );
  }
  return Array.from(new Set(items));
}

function primaryActionForRow(
  row: AdminLaunchReadinessProjection,
  actions: {
    onProvision: () => void;
    onPublish: () => void;
    onActivate: () => void;
    onPrepareLatestLaunchEvidence: () => void;
  }
): PrimaryAction {
  if (row.status === "admin_provisioning_required") {
    return {
      label: "Set up review process",
      description: "Admin must prepare the review path PM selected.",
      action: actions.onProvision,
    };
  }
  if (row.status === "form_publication_required") {
    if (!row.activation_candidate_can_publish) {
      return {
        label: "Prepare latest evidence for publishing",
        description:
          "The form is prepared. Prepare and approve the latest launch evidence, then publish the student form.",
        action: actions.onPrepareLatestLaunchEvidence,
        disabled: !row.activation_candidate_form_draft_id,
        disabledReason: !row.activation_candidate_form_draft_id
          ? "a launch form draft is available"
          : undefined,
      };
    }
    return {
      label: "Publish student form",
      description: "Make the approved form the student-facing version.",
      action: actions.onPublish,
    };
  }
  if (row.status === "form_activation_required") {
    return {
      label: "Open applications",
      description: row.activation_candidate_can_activate
        ? "Allow students to start applying with this form."
        : "The student form must be ready before applications can be opened.",
      action: actions.onActivate,
      disabled: !row.activation_candidate_can_activate,
      disabledReason: row.activation_candidate_blocker
        ? friendlyBlocker(row.activation_candidate_blocker)
        : "the student form is ready",
    };
  }
  if (row.status === "ready_for_launch" || row.activation_status === "active") {
    return {
      label: "Applications are open",
      description: "No launch action is needed right now.",
    };
  }
  if (row.status === "evidence_contract_confirmation_required") {
    return {
      label: "Approve document rules below",
      description: "Finish required documents before publishing.",
    };
  }
  return {
    label: "Waiting for setup",
    description: friendlyRowMessage(row),
  };
}

function StatusTile({
  title,
  detail,
  state,
  icon,
  owner,
  status,
  action,
}: {
  title: string;
  detail: string;
  state: TileState;
  icon?: ReactNode;
  owner?: string;
  status?: string;
  action?: string;
}) {
  const tone = {
    complete: "border-green-200 bg-green-50 text-green-900",
    attention: "border-amber-200 bg-amber-50 text-amber-900",
    waiting: "border-gray-200 bg-gray-50 text-gray-800",
    blocked: "border-red-200 bg-red-50 text-red-900",
  }[state];
  const Icon = state === "complete" ? CheckCircle2 : AlertCircle;

  return (
    <div className={`min-w-0 rounded-lg border p-3 ${tone}`}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-current">{icon ?? <Icon className="h-4 w-4" />}</span>
        <span className="min-w-0 break-words text-sm font-semibold">{title}</span>
      </div>
      {owner ? (
        <div className="mt-2 w-fit rounded-full border border-current/20 bg-white/60 px-2 py-0.5 text-[11px] font-medium">
          Owner: {owner}
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-medium">
        {status ? (
          <span className="rounded-full border border-current/20 bg-white/60 px-2 py-0.5">
            Status: {status}
          </span>
        ) : null}
        {action ? (
          <span className="rounded-full border border-current/20 bg-white/60 px-2 py-0.5">
            Action: {action}
          </span>
        ) : null}
      </div>
      <p className="mt-2 min-w-0 break-words text-sm opacity-90">{detail}</p>
    </div>
  );
}

function AdminTextInput({
  label,
  value,
  type = "text",
  placeholder = "",
  onChange,
}: {
  label: string;
  value: string;
  type?: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-xs font-medium text-gray-700">
      {label}
      <input
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-950"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function PendingStaffActivation({
  staff,
  otps,
  saving,
  onOtpChange,
  onSendCode,
  onActivate,
}: {
  staff: StaffUserProjection[];
  otps: Record<number, string>;
  saving: boolean;
  onOtpChange: (userId: number, otp: string) => void;
  onSendCode: (userId: number) => void;
  onActivate: (user: StaffUserProjection) => void;
}) {
  const pending = staff.filter((user) => !user.is_active || !user.is_verified);
  if (pending.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 rounded-lg border bg-white p-3">
      <div>
        <div className="font-medium text-gray-950">Finish staff activation</div>
        <p className="text-xs text-gray-600">
          Enter the OTP sent to the staff member, then activate access.
        </p>
      </div>
      <div className="grid gap-2">
        {pending.map((user) => (
          <div
            key={user.id}
            className="grid gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm lg:grid-cols-[minmax(0,1fr)_160px_120px_140px]"
          >
            <div className="min-w-0">
              <div className="truncate font-medium text-gray-950">{user.username}</div>
              <div className="text-xs text-gray-600">
                {friendlyRole(user.role)} · {user.is_verified ? "verified" : "needs OTP"} ·{" "}
                {user.is_active ? "active" : "inactive"}
              </div>
            </div>
            <input
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              value={otps[user.id] ?? ""}
              placeholder={user.is_verified ? "Already verified" : "OTP"}
              disabled={saving || user.is_verified}
              onChange={(event) => onOtpChange(user.id, event.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={saving || user.is_verified}
              onClick={() => onSendCode(user.id)}
            >
              Send OTP
            </Button>
            <Button type="button" size="sm" disabled={saving} onClick={() => onActivate(user)}>
              Activate
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LabeledSelect({
  label,
  value,
  placeholder,
  options = [],
  optionLabels = {},
  disabled = false,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options?: string[];
  optionLabels?: Record<string, string>;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const normalizedOptions = Array.from(new Set(options.filter(Boolean))).sort();

  return (
    <label className="grid gap-1 text-xs font-medium text-gray-700">
      {label}
      <select
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-950 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {normalizedOptions.map((option) => (
          <option key={option} value={option}>
            {optionLabels[option] ?? friendlyKey(option)}
          </option>
        ))}
      </select>
    </label>
  );
}

function friendlyRole(role: string): string {
  return ADMIN_STAFF_ROLE_OPTIONS.find((item) => item.value === role)?.label ?? friendlyKey(role);
}

function LabeledInput({
  label,
  value,
  placeholder,
  options = [],
  listId,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options?: string[];
  listId?: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  const normalizedOptions = Array.from(new Set(options.filter(Boolean))).sort();

  return (
    <label className="grid gap-1 text-xs font-medium text-gray-700">
      {label}
      <input
        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-950"
        placeholder={placeholder}
        value={value}
        list={listId}
        onChange={onChange}
      />
      {listId && normalizedOptions.length > 0 ? (
        <datalist id={listId}>
          {normalizedOptions.map((option) => (
            <option key={option} value={option}>
              {friendlyKey(option)}
            </option>
          ))}
        </datalist>
      ) : null}
    </label>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
          {icon}
          {label}
        </div>
        <div className="mt-2 text-3xl font-semibold text-gray-950">{value}</div>
        <div className="mt-1 text-sm text-gray-600">{detail}</div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: string }) {
  const labelMap: Record<string, string> = {
    pm_action_required: "PM action required",
    admin_provisioning_required: "Review process needs setup",
    evidence_contract_confirmation_required: "Documents need approval",
    form_publication_required: "Final publish step needed",
    form_activation_required: "Ready to open",
    ready_for_launch: "Open for applications",
    mismatch: "Needs review",
    document_rules_complete: "Documents ready",
    document_rules_missing: "Documents need attention",
    document_rules_waiting: "Waiting for PM form",
  };
  const tone =
    status === "ready_for_launch" || status === "document_rules_complete"
      ? "border-green-200 bg-green-50 text-green-800"
      : status === "admin_provisioning_required" || status === "form_publication_required"
        ? "border-blue-200 bg-blue-50 text-blue-800"
        : status === "mismatch"
          ? "border-red-200 bg-red-50 text-red-800"
          : "border-amber-200 bg-amber-50 text-amber-800";

  return (
    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${tone}`}>
      {labelMap[status] ?? status}
    </span>
  );
}

function ActivationStatusPill({ status }: { status: string }) {
  const labelMap: Record<string, string> = {
    active: "Student form is live",
    candidate_ready: "Ready to open",
    source_form_required: "Student form not open yet",
    approval_required: "Admin approval needed",
    candidate_missing: "Not ready yet",
  };
  const tone =
    status === "active" || status === "candidate_ready"
      ? "border-green-200 bg-green-50 text-green-800"
      : status === "source_form_required" || status === "approval_required"
        ? "border-amber-200 bg-amber-50 text-amber-800"
        : "border-gray-200 bg-gray-50 text-gray-700";

  return (
    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-medium ${tone}`}>
      {labelMap[status] ?? status}
    </span>
  );
}

function readinessRowKey(row: AdminLaunchReadinessProjection): string {
  return `${row.program_code}:${row.academic_year}`;
}

function documentRulesStatus(
  row: AdminLaunchReadinessProjection,
  contract?: EvidenceContractProjection
): string {
  if (row.evidence_requirements_status === "not_available") {
    return "document_rules_waiting";
  }
  if (contract?.status === "CONFIRMED" || documentRulesComplete(row)) {
    return "document_rules_complete";
  }
  return "document_rules_missing";
}

function friendlyRowMessage(row: AdminLaunchReadinessProjection): string {
  const direct: Record<string, string> = {
    pm_action_required: "PM needs to finish setup before Admin can launch.",
    admin_provisioning_required: "Admin needs to set up the selected review process.",
    evidence_contract_confirmation_required:
      "Admin needs to approve student document requirements.",
    form_publication_required: "The student form is ready to publish.",
    form_activation_required: "The published form is ready to open for students.",
    ready_for_launch: "Students can apply using this form.",
    mismatch: "This setup needs review before launch.",
  };
  return direct[row.status] ?? row.message ?? "Review this scholarship before launch.";
}

function friendlyProgramName(programCode: string): string {
  const value = friendlyKey(programCode);
  return value
    .split(" ")
    .map((word) =>
      word.length <= 3 && word === word.toUpperCase()
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");
}

function listOrNone(values: string[]): string {
  const filtered = values.filter(Boolean);
  return filtered.length > 0 ? filtered.join(", ") : "none selected";
}

function defaultFieldDecisionDraft(fieldKey: string): FieldDecisionDraft {
  return {
    step: 1,
    fieldKey,
    trustChoice: null,
    requirementKey: "",
    documentTypeKeys: [],
    methodKeys: [],
  };
}

function buildDecisionReason(draft: FieldDecisionDraft): string {
  if (draft.trustChoice === "declaration") {
    return `${friendlyKey(draft.requirementKey)} will use student declaration for launch.`;
  }
  return `${friendlyKey(draft.requirementKey)} requires supporting document before launch.`;
}

function validateFieldDecision(decision: FieldDecisionSubmit): string | null {
  if (!decision.fieldKey.trim()) {
    return "Choose the form field first.";
  }
  if (!decision.requirementKey.trim()) {
    return "This field does not have a known proof category yet. Use advanced support information.";
  }
  if (!decision.trustChoice) {
    return "Choose whether student declaration is enough or a document is required.";
  }
  if (decision.trustChoice === "document" && decision.documentTypeKeys.length === 0) {
    return "Choose at least one document students can upload.";
  }
  if (decision.trustChoice === "document" && decision.methodKeys.length === 0) {
    return "Choose at least one way DDO can verify this information.";
  }
  return null;
}

function fieldDecisionSummary(decision: SavedFieldDecision): string {
  if (decision.trustChoice === "declaration") {
    return "Student declaration accepted";
  }
  const documentCount = decision.documentTypeKeys.length;
  const methodCount = decision.methodKeys.length;
  return `${documentCount} accepted document${documentCount === 1 ? "" : "s"}, ${methodCount} verification option${methodCount === 1 ? "" : "s"}`;
}

function buildDecisionPayloads(
  decision: FieldDecisionSubmit
): EvidenceContractOverridePayload[] {
  const base = {
    field_key: decision.fieldKey,
    requirement_key: decision.requirementKey,
    reason: decision.reason,
  };
  if (decision.trustChoice === "declaration") {
    return [
      {
        override_type: "remove_field_requirement" as const,
        ...base,
        method_key: "",
        document_type_key: "",
      },
    ];
  }
  return [
    {
      override_type: "map_field_requirement" as const,
      ...base,
      method_key: "",
      document_type_key: "",
    },
    ...decision.documentTypeKeys.map((documentTypeKey) => ({
      override_type: "add_document_type" as const,
      field_key: "",
      requirement_key: decision.requirementKey,
      method_key: "",
      document_type_key: documentTypeKey,
      reason: decision.reason,
    })),
    ...decision.methodKeys.map((methodKey) => ({
      override_type: "enable_method" as const,
      field_key: "",
      requirement_key: decision.requirementKey,
      method_key: methodKey,
      document_type_key: "",
      reason: decision.reason,
    })),
  ];
}

function defaultOverrideDraft(
  row: AdminLaunchReadinessProjection
): EvidenceContractOverridePayload {
  const fieldKey = row.evidence_requirements.unresolved_fields[0] ?? "";
  return {
    override_type: "map_field_requirement",
    field_key: fieldKey,
    requirement_key: "",
    method_key: "",
    document_type_key: "",
    reason: "",
  };
}

function defaultFieldsForOverrideType(
  overrideType: EvidenceOverrideType
): Partial<EvidenceContractOverridePayload> {
  if (overrideType === "map_field_requirement" || overrideType === "remove_field_requirement") {
    return { method_key: "", document_type_key: "" };
  }
  if (overrideType === "enable_method" || overrideType === "disable_method") {
    return { field_key: "", document_type_key: "" };
  }
  return { field_key: "", method_key: "" };
}

function validateOverrideDraft(
  draft: EvidenceContractOverridePayload,
  row: AdminLaunchReadinessProjection
): string | null {
  const action = overrideActionLabel(draft.override_type);
  const fieldKey = String(draft.field_key ?? "").trim();
  const requirementKey = String(draft.requirement_key ?? "").trim();
  const methodKey = String(draft.method_key ?? "").trim();
  const documentTypeKey = String(draft.document_type_key ?? "").trim();
  const reason = String(draft.reason ?? "").trim();

  if (
    (draft.override_type === "map_field_requirement" ||
      draft.override_type === "remove_field_requirement") &&
    !fieldKey
  ) {
    return `Choose the form field before you ${action.toLowerCase()}.`;
  }
  if (!requirementKey) {
    return `Choose the proof category before you ${action.toLowerCase()}.`;
  }
  if (
    (draft.override_type === "enable_method" ||
      draft.override_type === "disable_method") &&
    !methodKey
  ) {
    return `Choose the verification method before you ${action.toLowerCase()}.`;
  }
  if (
    (draft.override_type === "add_document_type" ||
      draft.override_type === "remove_document_type") &&
    !documentTypeKey
  ) {
    return `Choose the document type before you ${action.toLowerCase()}.`;
  }
  if (!reason) {
    return "Add a short reason for this document requirement change.";
  }
  if (
    fieldKey &&
    row.evidence_requirements.requested_fields.length > 0 &&
    !row.evidence_requirements.requested_fields.includes(fieldKey)
  ) {
    return `${friendlyKey(fieldKey)} is not a field in this PM form. Choose one of the listed form fields.`;
  }
  return null;
}

function contractStatusLabel(status: string): string {
  const labelMap: Record<string, string> = {
    DRAFT: "Draft document rules",
    READY_FOR_CONFIRMATION: "Ready for Admin approval",
    CONFIRMED: "Approved document rules",
    SUPERSEDED: "Replaced by newer rules",
  };
  return labelMap[status] ?? status;
}

function overrideActionLabel(type: EvidenceOverrideType): string {
  const labelMap: Record<EvidenceOverrideType, string> = {
    map_field_requirement: "Set proof needed for a field",
    remove_field_requirement: "Mark a field as no proof needed",
    enable_method: "Allow another verification method",
    disable_method: "Remove a verification method",
    add_document_type: "Add an accepted document",
    remove_document_type: "Remove an accepted document",
  };
  return labelMap[type];
}

function overrideHelpText(type: EvidenceOverrideType): string {
  const helpMap: Record<EvidenceOverrideType, string> = {
    map_field_requirement:
      "Use this when a form field is already present but students still need to prove it before launch.",
    remove_field_requirement:
      "Use this when a form field should stay on the form but should not need this proof.",
    enable_method:
      "Use this to allow another way to verify an existing proof category.",
    disable_method:
      "Use this to remove a verification method that should not be accepted for this launch.",
    add_document_type:
      "Use this to add another document students may upload for an existing proof category.",
    remove_document_type:
      "Use this to remove a document that students should not upload for this launch.",
  };
  return helpMap[type];
}

function friendlyKey(value: string): string {
  const direct: Record<string, string> = {
    aadhaar_number: "Aadhaar number",
    aadhaar_card: "Aadhaar card",
    AADHAAR_EKYC: "Aadhaar eKYC",
    academic_marks_25_26: "Academic marks 25/26",
    academic_score: "Academic score",
    applicant_name: "Applicant name",
    API_VERIFICATION: "Trusted API check",
    bank_account_proof: "Bank account proof",
    bank_passbook: "Bank passbook",
    bank_statement: "Bank statement",
    BANK_VALIDATION: "Bank validation",
    disability_certificate: "Disability certificate",
    disability_proof: "Disability proof",
    district: "District",
    DOCUMENT_UPLOAD: "Document upload",
    domicile_certificate: "Domicile certificate",
    education_proof: "Education proof",
    family_income: "Family income",
    guardian_name: "Guardian name",
    identity_proof: "Identity proof",
    income_certificate: "Income certificate",
    income_proof: "Income proof",
    MANUAL_VERIFICATION: "Manual verification",
    marksheet: "Marksheet",
    OCR_EXTRACTION: "OCR extraction",
    phone_number: "Phone number",
    principal_name: "Principal name",
    ration_card: "Ration card",
    REGISTRY_CHECK: "Registry check",
    residency_proof: "Residency proof",
    salary_slip: "Salary slip",
    SELF_DECLARATION: "Self declaration",
    state: "State",
  };
  if (direct[value]) {
    return direct[value];
  }
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sourceLabel(value: string): string {
  if (value === "governance_default") {
    return "Standard recommendation";
  }
  if (value === "admin_override") {
    return "Admin adjustment";
  }
  if (value === "mixed") {
    return "Standard plus Admin adjustment";
  }
  return friendlyKey(value);
}

function friendlyBlocker(value: string): string {
  if (value.toLowerCase().includes("approval evidence must be current")) {
    return "Final approval evidence is not complete yet. Review and approve the latest launch evidence before publishing.";
  }
  if (value.startsWith("unresolved_field:")) {
    return `${friendlyKey(value.replace("unresolved_field:", ""))} needs a document decision`;
  }
  const direct: Record<string, string> = {
    activation_concurrence_incomplete: "Admin approval is not complete",
    activation_publishability_blocked: "The student form is not publishable yet",
    activation_stale_evidence: "The form changed after approval; review it again",
    contract_not_confirmed: "Document rules still need Admin approval",
    no_evidence_contract: "Load and approve the document rules first",
    source_form_required: "Publish the student form before opening applications",
  };
  return direct[value] ?? friendlyKey(value);
}

function availableEvidenceRequirements(
  row: AdminLaunchReadinessProjection,
  contract?: EvidenceContractProjection
): EvidenceRequirementProjection[] {
  const byKey = new Map<string, EvidenceRequirementProjection>();
  for (const requirement of row.evidence_requirements.available_requirements ?? []) {
    byKey.set(requirement.requirement_key, requirement);
  }
  for (const requirement of contract?.current_revision.evidence_requirements
    .available_requirements ?? []) {
    byKey.set(requirement.requirement_key, requirement);
  }
  return Array.from(byKey.values()).sort((a, b) =>
    requirementDisplayName(a).localeCompare(requirementDisplayName(b))
  );
}

function requirementDisplayName(requirement: EvidenceRequirementProjection): string {
  return requirement.label || friendlyKey(requirement.requirement_key);
}

function optionLabelsForRequirements(
  requirements: EvidenceRequirementProjection[]
): Record<string, string> {
  return Object.fromEntries(
    requirements.map((requirement) => [
      requirement.requirement_key,
      requirementDisplayName(requirement),
    ])
  );
}

function optionLabelsForMethods(
  selectedRequirement?: EvidenceRequirementProjection | null,
  fallbackRequirements: EvidenceRequirementProjection[] = []
): Record<string, string> {
  const methods = selectedRequirement
    ? selectedRequirement.accepted_methods
    : fallbackRequirements.flatMap((requirement) => requirement.accepted_methods);
  return Object.fromEntries(
    methods.map((method) => [method.method_key, method.label || friendlyKey(method.method_key)])
  );
}

function optionLabelsForDocumentTypes(
  selectedRequirement?: EvidenceRequirementProjection | null,
  fallbackRequirements: EvidenceRequirementProjection[] = []
): Record<string, string> {
  const documents = selectedRequirement
    ? selectedRequirement.accepted_document_types
    : fallbackRequirements.flatMap((requirement) => requirement.accepted_document_types);
  return Object.fromEntries(
    documents.map((document) => [
      document.document_type_key,
      document.label || friendlyKey(document.document_type_key),
    ])
  );
}

function affectedFieldsForRequirement(
  row: AdminLaunchReadinessProjection,
  contract: EvidenceContractProjection | undefined,
  requirementKey: string,
  fallbackFieldKey: string
): string[] {
  const fields = new Set<string>();
  if (!requirementKey) {
    fields.add(fallbackFieldKey);
    return Array.from(fields).filter(Boolean).sort();
  }
  for (const requirement of row.evidence_requirements.resolved_requirements) {
    if (requirement.requirement_key === requirementKey) {
      requirement.required_for_fields.forEach((fieldKey) => fields.add(fieldKey));
    }
  }
  for (const requirement of contract?.current_revision.evidence_requirements
    .resolved_requirements ?? []) {
    if (requirement.requirement_key === requirementKey) {
      requirement.required_for_fields.forEach((fieldKey) => fields.add(fieldKey));
    }
  }
  fields.add(fallbackFieldKey);
  return Array.from(fields).filter(Boolean).sort();
}

function evidenceMethodOptions(
  selectedRequirement?: EvidenceRequirementProjection | null,
  fallbackRequirements: EvidenceRequirementProjection[] = []
): string[] {
  const methods = selectedRequirement
    ? selectedRequirement.accepted_methods
    : fallbackRequirements.flatMap((requirement) => requirement.accepted_methods);
  return Array.from(new Set(methods.map((method) => method.method_key).filter(Boolean))).sort();
}

function evidenceDocumentOptions(
  selectedRequirement?: EvidenceRequirementProjection | null,
  fallbackRequirements: EvidenceRequirementProjection[] = []
): string[] {
  const documents = selectedRequirement
    ? selectedRequirement.accepted_document_types
    : fallbackRequirements.flatMap((requirement) => requirement.accepted_document_types);
  return Array.from(
    new Set(documents.map((document) => document.document_type_key).filter(Boolean))
  ).sort();
}

function readError(caught: unknown, fallback: string): string {
  if (typeof caught === "object" && caught !== null && "response" in caught) {
    const response = (
      caught as { response?: { status?: number; data?: { detail?: unknown } } }
    ).response;
    const detail = response?.data?.detail;
    if (response?.status === 422) {
      return friendlyValidationError(detail, fallback);
    }
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (
      typeof detail === "object" &&
      detail !== null &&
      "message" in detail &&
      typeof detail.message === "string" &&
      detail.message.trim()
    ) {
      return detail.message;
    }
  }
  if (caught instanceof Error && caught.message.trim()) {
    return caught.message;
  }
  return fallback;
}

function friendlyValidationError(detail: unknown, fallback: string): string {
  if (Array.isArray(detail)) {
    const missing = detail
      .map((item) => {
        if (typeof item !== "object" || item === null) {
          return null;
        }
        const loc = "loc" in item && Array.isArray(item.loc) ? item.loc : [];
        const field = loc[loc.length - 1];
        return typeof field === "string" ? friendlyKey(field) : null;
      })
      .filter(Boolean);
    if (missing.length > 0) {
      return `That request is missing required information: ${missing.join(", ")}. Fill those fields and try again.`;
    }
  }
  return `${fallback} Some required information was missing or invalid.`;
}
